/**
 * client/src/lib/usageApi.ts — the ONE typed client module for the monthly report limit (§6).
 *
 * WHY ONE MODULE. The pill in the top bar, the tile on Home and the in-flow line above "Generate"
 * all have to show the SAME numbers, or they read as three different opinions about how many
 * reports are left. `GET /api/me/usage` is the one source of truth on the server; this file is the
 * one place `/next` parses its response, so A, B and C can never disagree about what a field means.
 *
 * `limit: null` MEANS ADMIN, NOT LIMITED (owner decision: "Admins are never limited"). It is not a
 * missing value and never coerced to a number — every helper below treats `null` as its own case
 * rather than defaulting it to 10 or to 0, the same discipline `usersApi.ts`'s `last_seen_at`
 * follows for "never signed in".
 *
 * COLOR IS NEVER THE ONLY SIGNAL. Every helper that returns a tone is paired with text that states
 * the number — the pill, the tile and the in-flow line all put the figure in words, and the tone
 * only repeats it.
 */
import { apiFetch, ApiError } from "./auth";

export type UsageKind = "generate" | "regenerate";

export interface UsageItem {
  date: string;
  /** Null while a build using this slot is still running and has not named its subject yet
   * (`report_limit.py`'s own words: "never a placeholder"). Render a calm "not yet known" note,
   * never the literal string "null" and never a fabricated address. */
  address: string | null;
  kind: UsageKind;
  /** Present on the real server response; used as a stable list key when available. */
  job_id?: string;
}

export interface Usage {
  used: number;
  /** `null` = admin, not limited. */
  limit: number | null;
  /** `null` exactly when `limit` is `null` — there is no "remaining" of an unlimited account. */
  remaining: number | null;
  /** `null` exactly when `limit` is `null`. 0–100, already rounded by the server. */
  percent: number | null;
  /** ISO date (midnight America/Chicago on the 1st of next month). */
  resets_on: string;
  /** This month's counted reports, newest first, released rows already excluded by the server. */
  items: UsageItem[];
  /** Carried straight through from the server for a caller that wants it; every surface in this
   * slice derives admin-ness from `limit === null` instead, which `report_limit.py` documents as
   * the same fact. */
  is_admin?: boolean;
  /** "credits": billing is on and report credits decide (server `billing_credits.py`); `limit`
   * is then null and the account is NOT an admin. */
  limit_source?: "admin" | "user" | "group" | "default" | "credits";
}

export interface LimitReachedBody {
  error: "limit_reached";
  used: number;
  limit: number;
  resets_on: string;
  message: string;
}

export const getUsage = () => apiFetch<Usage>("/api/me/usage");

export interface SetReportLimitBody {
  /** `null` (or omitted) clears the override back to the default of 10. */
  limit: number | null;
  reason: string;
}

export const setUserReportLimit = (userId: string, body: SetReportLimitBody) =>
  apiFetch<{ user: unknown }>(`/api/admin/users/${encodeURIComponent(userId)}/report-limit`, {
    method: "PUT",
    body,
  });

/** Narrows a caught error to the server's typed refusal, so a caller can render it in place
 * rather than a generic "that didn't work." The real app mounts `report_limit.py`'s exception
 * handler, so the body is `{"error": "limit_reached", ...}` at the TOP level — but the module's
 * own docstring says a bare-router test context can nest the same object under `detail`, and
 * tells callers to accept both, so this checks both shapes. */
const REFUSALS = new Set<unknown>(["limit_reached", "out_of_credits"]);

/** Where an out-of-credits refusal sends the person: buy a pack, upgrade, or turn on overage. */
export const OUT_OF_CREDITS_URL = "/billing/credits?out=1";

/** True for the server's out-of-credits sentence, which always names the ways out ("Buy a report
 * pack, change your plan, or turn on pay-per-extra-report."). The monthly limit's sentences never
 * mention either. */
export function isCreditsMessage(message: string): boolean {
  return /report pack|pay-per-extra-report/i.test(message);
}

export function isLimitReached(error: unknown): error is ApiError & { body: LimitReachedBody } {
  if (!(error instanceof ApiError) || typeof error.body !== "object" || error.body === null) {
    return false;
  }
  const body = error.body as { error?: unknown; detail?: { error?: unknown } };
  // `out_of_credits` (HTTP 402, billing, dark behind VALUBRIEF_BILLING_ENABLED) is the same kind of
  // refusal: a quota, rendered in place with the server's own sentence. `billing_credits.py`
  // raises it as a subclass of the monthly limit's refusal, so the shape is identical.
  return REFUSALS.has(body.error) || REFUSALS.has(body.detail?.error);
}

/** The three states the design calls out. Normal is also what an unlimited (admin) account gets,
 * since there is nothing to warn about. */
export type UsageTone = "normal" | "warn" | "limit";

export function usageTone(usage: Usage): UsageTone {
  if (usage.limit === null || usage.percent === null) return "normal";
  if (usage.percent >= 100) return "limit";
  if (usage.percent >= 80) return "warn";
  return "normal";
}

/** True once a limited account has zero reports left. Always false for an admin — the owner's
 * rule is that admins are never limited, so there is no "at limit" state to render for one. */
export function atUsageLimit(usage: Usage | null | undefined): usage is Usage {
  return !!usage && usage.limit !== null && (usage.remaining ?? 1) <= 0;
}

/**
 * "Nov 1" from an ISO *date* (no time component expected). Parsed from the digits directly rather
 * than through `new Date(iso)`: that constructor reads a bare "YYYY-MM-DD" as UTC midnight, and a
 * browser west of UTC would then print the day BEFORE the real reset — a promise about when
 * access comes back must not drift by a day depending on the reader's clock.
 */
export function resetDateShort(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[Number(m[2]) - 1];
  return month ? `${month} ${Number(m[3])}` : iso;
}
